import {
  addDays,
  addHours,
  addMinutes,
  addMonths,
  addWeeks,
  addYears,
  differenceInCalendarDays,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInMonths,
  differenceInSeconds,
  differenceInWeeks,
  differenceInYears,
  endOfDay,
  endOfWeek,
  format,
  formatISO,
  formatRelative,
  fromUnixTime,
  getUnixTime,
  getWeek,
  isAfter,
  isBefore,
  isEqual,
  isSameDay,
  parse,
  setDay,
  setWeek,
  startOfDay,
  startOfWeek,
  subDays,
  subHours,
  subMinutes,
  subMonths,
  subWeeks,
  subYears
} from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import enUS from 'date-fns/locale/en-US'
import { capitalizeStringUtil } from '../string'
import { getDate } from './get-date'

// https://date-fns.org/docs/I18n-Contribution-Guide#formatrelative
// https://github.com/date-fns/date-fns/blob/master/src/locale/en-US/_lib/formatRelative/index.js
// https://github.com/date-fns/date-fns/issues/1218
// https://stackoverflow.com/questions/47244216/how-to-customize-date-fnss-formatrelative
const formatRelativeLocaleWithoutTimeObj = {
  lastWeek: '\'Last\' eeee',
  yesterday: '\'Yesterday\'',
  today: '\'Today\'',
  tomorrow: '\'Tomorrow\'',
  nextWeek: 'eeee',
  other: 'PP'
}

const formatRelativeLocale = {
  lastWeek: '\'Last\' eeee \'at\' p',
  yesterday: '\'Yesterday at\' p',
  today: '\'Today at\' p',
  tomorrow: '\'Tomorrow at\' p',
  nextWeek: 'eeee \'at\' p',
  other: 'PPpp'
}

const localeWithoutTime = {
  ...enUS,
  formatRelative: (token) => formatRelativeLocaleWithoutTimeObj[token]
}

const locale = {
  ...enUS,
  formatRelative: (token) => formatRelativeLocale[token]
}

export function formatRelativeWithoutTimeDateUtil (date, baseDate) {
  return formatRelativeDateUtil(date, baseDate, { locale: localeWithoutTime })
}

export function getTimeDateUtil (date) {
  date = getDate(date)
  return format(date, 'HH:mm')
}

export function setDayDateUtil (date, day) {
  date = getDate(date)
  return setDay(date, day)
}

export function getUnixTimeDateUtil (date) {
  date = getDate(date)
  return getUnixTime(date)
}

export function fromUnixTimeDateUtil (date) {
  date = getDate(date)
  return fromUnixTime(date)
}

export function isTodayDateUtil (date) {
  date = getDate(date)
  return isSameDay(new Date(), date)
}

export function isSameDayDateUtil (date, dateB) {
  date = getDate(date)
  return isSameDay(date, dateB)
}

export function addDaysDateUtil (date, days) {
  date = getDate(date)
  return addDays(date, days)
}

export function subDaysDateUtil (date, days) {
  date = getDate(date)
  return subDays(date, days)
}

export function addHoursDateUtil (date, days) {
  date = getDate(date)
  return addHours(date, days)
}

export function subHoursDateUtil (date, days) {
  date = getDate(date)
  return subHours(date, days)
}

export function addMinutesDateUtil (date, days) {
  date = getDate(date)
  return addMinutes(date, days)
}

export function subMinutesDateUtil (date, days) {
  date = getDate(date)
  return subMinutes(date, days)
}

export function addMonthsDateUtil (date, months) {
  date = getDate(date)
  return addMonths(date, months)
}

export function subMonthsDateUtil (date, months) {
  date = getDate(date)
  return subMonths(date, months)
}

export function addWeeksDateUtil (date, weeks) {
  date = getDate(date)
  return addWeeks(date, weeks)
}

export function subWeeksDateUtil (date, weeks) {
  date = getDate(date)
  return subWeeks(date, weeks)
}

export function addYearsDateUtil (date, weeks) {
  date = getDate(date)
  return addYears(date, weeks)
}

export function subYearsDateUtil (date, weeks) {
  date = getDate(date)
  return subYears(date, weeks)
}

export function getUtcToTimezoneDateUtil (isoDate, timezone) {
  return utcToZonedTime(isoDate, timezone)
}

export function setHoursMinutesAndSecondsDateUtil (date, {
  hours = 0,
  minutes = 0,
  seconds = 0,
  milliseconds = 0
}) {
  date = getDate(date)
  date.setHours(hours)
  date.setMinutes(minutes)
  date.setSeconds(seconds)
  date.setMilliseconds(milliseconds)
  return date
}

export function createISOStrDateUtil (date) {
  date = getDate(date)
  if (!date) {
    return (new Date()).toISOString()
  }

  return (new Date(date)).toISOString()
}

export function createISODateUtil (date) {
  date = getDate(date)
  return new Date(createISOStrDateUtil(date))
}

export function getDayDateStrDateUtil (date) {
  date = getDate(date)
  return format(date, 'yyyy-MM-dd')
}

export function getLocalDayDateStrDateUtil (date) {
  date = getDate(date)
  return format(date, 'P')
}

export function formatRelativeDateUtil (date, baseDate = new Date(), options = {
  locale
}) {
  date = getDate(date)
  return capitalizeStringUtil(formatRelative(date, baseDate, options))
}

export function getWeekStrDateUtil (date) {
  date = getDate(date)
  return format(date, 'yyyy-I')
}

export function getDateFromWeekStrDateUtil (weekStr) {
  const [year, week] = weekStr.split('-')
  const date = new Date()
  date.setFullYear(year)
  return parseDateUtil(week, 'I', date)
}

export function getMonthStrDateUtil (date) {
  date = getDate(date)
  return format(date, 'yyyy-M')
}

export function getWeekNumberDateUtil (date) {
  date = getDate(date)
  return getWeek(date)
}

export function getTimeStrDateUtil (date) {
  date = getDate(date)
  return format(date, 'p')
}

export function formatISODateUtil (date) {
  date = getDate(date)
  return formatISO(date)
}

export function formatDateUtil (date, formatStr) {
  date = getDate(date)
  return format(date, formatStr)
}

export function parseDateUtil (dateString, formatString, date) {
  date = getDate(date)
  return parse(dateString, formatString, date)
}

export function setWeekDateUtil (date, week) {
  date = getDate(date)
  return setWeek(date, week)
}

export function startOfDayDateUtil (date) {
  date = getDate(date)
  return startOfDay(date)
}

export function endOfDayDateUtil (date) {
  date = getDate(date)
  return endOfDay(date)
}

export function startOfWeekDateUtil (date, options) {
  date = getDate(date)
  return startOfWeek(date, options)
}

export function endOfWeekDateUtil (date, options) {
  date = getDate(date)
  return endOfWeek(date, options)
}

export function differenceInCalendarDaysDateUtil (dateLeft, dateRight) {
  dateLeft = getDate(dateLeft)
  dateRight = getDate(dateRight)
  return differenceInCalendarDays(
    createISODateUtil(dateLeft),
    createISODateUtil(dateRight)
  )
}

export function differenceInDaysDateUtil (dateLeft, dateRight) {
  dateLeft = getDate(dateLeft)
  dateRight = getDate(dateRight)
  return differenceInDays(
    createISODateUtil(dateLeft),
    createISODateUtil(dateRight)
  )
}

export function differenceInHoursDateUtil (dateLeft, dateRight) {
  dateLeft = getDate(dateLeft)
  dateRight = getDate(dateRight)
  return differenceInHours(
    createISODateUtil(dateLeft),
    createISODateUtil(dateRight)
  )
}

export function differenceInSecondsDateUtil (dateLeft, dateRight) {
  dateLeft = getDate(dateLeft)
  dateRight = getDate(dateRight)
  return differenceInSeconds(
    createISODateUtil(dateLeft),
    createISODateUtil(dateRight)
  )
}

export function differenceInYearsDateUtil (dateLeft, dateRight) {
  dateLeft = getDate(dateLeft)
  dateRight = getDate(dateRight)
  return differenceInYears(
    createISODateUtil(dateLeft),
    createISODateUtil(dateRight)
  )
}

export function differenceInMonthsDateUtil (dateLeft, dateRight) {
  dateLeft = getDate(dateLeft)
  dateRight = getDate(dateRight)
  return differenceInMonths(
    createISODateUtil(dateLeft),
    createISODateUtil(dateRight)
  )
}

export function differenceInWeeksDateUtil (dateLeft, dateRight) {
  dateLeft = getDate(dateLeft)
  dateRight = getDate(dateRight)
  return differenceInWeeks(
    createISODateUtil(dateLeft),
    createISODateUtil(dateRight)
  )
}

export function differenceInMinutesDateUtil (dateLeft, dateRight) {
  dateLeft = getDate(dateLeft)
  dateRight = getDate(dateRight)
  return differenceInMinutes(
    createISODateUtil(dateLeft),
    createISODateUtil(dateRight)
  )
}

export function isAfterDateUtil (dateLeft, dateRight) {
  dateLeft = getDate(dateLeft)
  dateRight = getDate(dateRight)
  return isAfter(
    createISODateUtil(dateLeft),
    createISODateUtil(dateRight)
  )
}

export function isEqualDateUtil (dateLeft, dateRight) {
  dateLeft = getDate(dateLeft)
  dateRight = getDate(dateRight)
  return isEqual(
    createISODateUtil(dateLeft),
    createISODateUtil(dateRight)
  )
}

export function isBeforeDateUtil (dateLeft, dateRight) {
  dateLeft = getDate(dateLeft)
  dateRight = getDate(dateRight)
  return isBefore(
    createISODateUtil(dateLeft),
    createISODateUtil(dateRight)
  )
}

export * from './get-year-str-date'
export * from './format-day-with-ordinal-indicator'
